// @tennis-matches/services/governance
// Access Requests (Phase 1): request/approval layer before role_bindings.

import { getSupabaseClient } from "../supabase/index.js";
import { callGovernanceRpc } from "./governance-rpc-call.js";
import {
  APP_CONFIG_SCHEMA_REST_KEY,
  isAppConfigSchemaUnavailableError,
  isMissingRestResourceError,
  isRestEndpointUnavailable,
  markRestEndpointUnavailable,
} from "../supabase/rest-endpoint-availability.js";
import { isAuthUserEmailConfirmed } from "../auth/registration-legal.js";
import { expandSelfSignupUiRolesForPersist } from "./self-signup-roles.js";
import { filterSelfAccessRolesForMinorMarketplace } from "./marketplace-minor-roles.js";
import { loadMyMarketplacePersonAgeRemote } from "./marketplace-person-age.service.js";
import { REGISTRATION_ONBOARDING_OUTCOME } from "./registration-onboarding.service.js";
import { rejectInvalidTenantScopeId } from "./scopeIdValidation.js";
import { notifyAccessRequestAdminsRemote } from "./access-request-notify-admin-api.js";
import { joinExistingTenantSelfServiceRemote } from "./self-service-clubs.service.js";

/** Scope-ID für Selbstregistrierung ohne Vereinsbindung (nur Access-Request, Freigabe durch Admin). */
export const INDEPENDENT_TRAINER_SCOPE_ID = "__independent_trainer__";

/** Platzhalter-scope_id, wenn ein neuer Verein nur als Wunsch (Metadaten) eingereicht wird — kein Tenant wird angelegt. */
export const NEW_CLUB_PENDING_SCOPE_ID = "__pending_new_club__";

const SCHEMA = "app_config";
const T_REQUESTS = "access_requests";
const ACCESS_REQUESTS_REST_KEY = "table:app_config.access_requests";

function __asStr(v) {
  return String(v ?? "").trim();
}

/** Positivliste + Minderjährigen-Filter für Self-Access-Requests. */
async function __sanitizeSelfAccessRolesForCurrentUser(rolesRaw) {
  const roles = expandSelfSignupUiRolesForPersist(rolesRaw);
  const ageRes = await loadMyMarketplacePersonAgeRemote();
  if (ageRes?.ageStatus === "minor") {
    return filterSelfAccessRolesForMinorMarketplace(roles);
  }
  return roles;
}

function __db(supabase) {
  return supabase && typeof supabase.schema === "function"
    ? supabase.schema(SCHEMA)
    : supabase;
}

function __isMissingRpcError(error) {
  const code = __asStr(error?.code);
  const msg = __asStr(error?.message).toLowerCase();
  return (
    code === "42883" ||
    code === "PGRST202" ||
    msg.includes("could not find the function") ||
    msg.includes("function not found") ||
    msg.includes("undefined function")
  );
}

async function __loadMyAccessRequestsViaRpc(supabase) {
  try {
    const { data, error } = await supabase.rpc("list_my_access_requests");
    if (!error && data) {
      if (Array.isArray(data)) {
        return { ok: true, requests: data, via: "rpc" };
      }
      if (Array.isArray(data?.requests)) {
        return { ok: true, requests: data.requests, via: "rpc" };
      }
    }
    if (error && !__isMissingRpcError(error)) {
      return { ok: false, reason: "rpc-error", error };
    }
  } catch (e) {
    return { ok: false, reason: "rpc-exception", error: e };
  }
  return null;
}

function __isMissingTableError(error) {
  return (
    isMissingRestResourceError(error) ||
    isAppConfigSchemaUnavailableError(error)
  );
}

function __accessRequestsUnavailable() {
  return (
    isRestEndpointUnavailable(ACCESS_REQUESTS_REST_KEY) ||
    isRestEndpointUnavailable(APP_CONFIG_SCHEMA_REST_KEY)
  );
}

function __markAccessRequestsUnavailable(error) {
  if (error && !__isMissingTableError(error)) return;
  markRestEndpointUnavailable(ACCESS_REQUESTS_REST_KEY);
  if (!error || isAppConfigSchemaUnavailableError(error)) {
    markRestEndpointUnavailable(APP_CONFIG_SCHEMA_REST_KEY);
  }
}

function __supabaseErrorInfo(error) {
  if (!error) return null;
  return {
    message: __asStr(error?.message),
    code: __asStr(error?.code),
    details: __asStr(error?.details),
    hint: __asStr(error?.hint),
    status: Number(error?.status || 0) || null,
  };
}

function __isSelfPendingAccessPayload(row) {
  const requester = __asStr(row?.requester_user_id);
  const target = __asStr(row?.target_user_id);
  const status = __asStr(row?.status) || "pending";
  return Boolean(
    requester && target && requester === target && status === "pending",
  );
}

async function __createSelfAccessRequestViaRpc(supabase, row) {
  const { data, error } = await callGovernanceRpc(
    supabase,
    "create_self_access_request",
    {
      p_scope: __asStr(row.scope) || "tenant",
      p_scope_id: __asStr(row.scope_id),
      p_requested_roles: Array.isArray(row.requested_roles)
        ? row.requested_roles
        : [],
      p_requested_capabilities:
        row.requested_capabilities &&
        typeof row.requested_capabilities === "object"
          ? row.requested_capabilities
          : {},
      p_requested_club_ids: Array.isArray(row.requested_club_ids)
        ? row.requested_club_ids
        : null,
      p_note: row.note == null ? null : __asStr(row.note) || null,
    },
  );

  if (!error && data && typeof data === "object") {
    return { ok: true, request: data };
  }
  if (
    error &&
    (__isMissingRpcError(error) || isAppConfigSchemaUnavailableError(error))
  ) {
    return null;
  }
  if (error) {
    return {
      ok: false,
      reason: "rpc-error",
      error,
      errorInfo: __supabaseErrorInfo(error),
    };
  }
  return null;
}

async function __getAuthUserId(supabase) {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) return { ok: false, reason: "no-user", error };
    const uid = data?.user?.id || null;
    return uid
      ? { ok: true, userId: String(uid) }
      : { ok: false, reason: "no-user" };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export async function createAccessRequestRemote(payload) {
  const row = payload && typeof payload === "object" ? payload : null;
  if (!row) return { ok: false, reason: "invalid-payload" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  if (__isSelfPendingAccessPayload(row)) {
    const rpcRes = await __createSelfAccessRequestViaRpc(supabase, row);
    if (rpcRes?.ok) return rpcRes;
    if (rpcRes && !rpcRes.ok) return rpcRes;
  }

  const db = __db(supabase);
  if (!db) {
    if (__isSelfPendingAccessPayload(row)) {
      return {
        ok: false,
        reason: "rpc-missing",
        errorInfo: { message: "create_self_access_request unavailable" },
      };
    }
    return { ok: false, reason: "no-db" };
  }

  const { data, error } = await db
    .from(T_REQUESTS)
    .insert(row)
    .select("*")
    .single();

  if (error) {
    const errorInfo = __supabaseErrorInfo(error);
    if (__isMissingTableError(error))
      return { ok: false, reason: "table-missing", error, errorInfo };
    return { ok: false, reason: "db-error", error, errorInfo };
  }

  return { ok: true, request: data || null };
}

/**
 * Minimaler Onboarding-Helper (Phase 1):
 * Erstellt eine Self-Onboarding Access Request (requester_user_id = target_user_id = auth.uid()).
 * Schreibt ausschließlich nach app_config.access_requests (niemals role_bindings).
 */
export async function createSelfAccessRequestRemote({
  scope = "tenant",
  scopeId,
  requestedRoles,
  requestedCapabilities,
  requestedClubIds,
  note = null,
} = {}) {
  const s = __asStr(scope) || "tenant";
  const sid = __asStr(scopeId);
  const rolesRaw = Array.isArray(requestedRoles)
    ? requestedRoles.map(__asStr).filter(Boolean)
    : [];
  const roles = await __sanitizeSelfAccessRolesForCurrentUser(rolesRaw);
  const caps =
    requestedCapabilities && typeof requestedCapabilities === "object"
      ? requestedCapabilities
      : {};
  const clubIds = Array.isArray(requestedClubIds)
    ? requestedClubIds.map(__asStr).filter(Boolean)
    : [];
  const n = note == null ? null : __asStr(note);

  const requestedAppKeys = Array.isArray(caps.requested_app_keys)
    ? caps.requested_app_keys.map(__asStr).filter(Boolean)
    : [];

  if (!sid) return { ok: false, reason: "missing-scope" };
  const scopeErr = rejectInvalidTenantScopeId(s, sid, {
    allowSystemPlaceholders: true,
  });
  if (scopeErr) return scopeErr;
  if (!roles.length && !requestedAppKeys.length)
    return { ok: false, reason: "missing-roles" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const uidRes = await __getAuthUserId(supabase);
  if (!uidRes?.ok)
    return {
      ok: false,
      reason: uidRes?.reason || "no-user",
      error: uidRes?.error,
    };

  const payload = {
    requester_user_id: uidRes.userId,
    target_user_id: uidRes.userId,
    scope: s,
    scope_id: sid,
    requested_roles: roles,
    requested_capabilities: caps,
    requested_club_ids: clubIds.length ? clubIds : [sid],
    status: "pending",
    note: n,
  };

  const res = await createAccessRequestRemote(payload);
  // keep payload for DEV diagnostics in callers (no UI strings here)
  if (!res?.ok) return { ...res, payload, userId: uidRes.userId };

  // Best-effort: Vereinsadmins benachrichtigen — Fehler hier brechen den Flow NICHT,
  // weil die Anfrage in der DB schon liegt und Admins sie auch ohne Mail bearbeiten können.
  const createdRequestId = __asStr(res?.request?.id);
  let notifyResult = null;
  if (createdRequestId) {
    try {
      notifyResult = await notifyAccessRequestAdminsRemote({
        requestId: createdRequestId,
      });
    } catch (e) {
      notifyResult = { ok: false, reason: String(e?.message || e) };
    }
  }
  return { ...res, payload, userId: uidRes.userId, notifyResult };
}

/**
 * Nach E-Mail-Registrierung: liest `user_metadata.pending_self_access_request`,
 * erzeugt genau einen Self-Access-Request (pending), markiert Metadaten als angewendet.
 */
export async function applyPendingSignupAccessRequestRemote() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data: authData, error: authErr } = await supabase.auth.getUser();
  if (authErr || !authData?.user?.id)
    return { ok: false, reason: "no-user", error: authErr };

  const user = authData.user;
  const meta =
    user.user_metadata && typeof user.user_metadata === "object"
      ? user.user_metadata
      : {};
  const pending = meta.pending_self_access_request;
  if (!pending || typeof pending !== "object")
    return { ok: true, skipped: true };
  if (pending.applied === true) return { ok: true, skipped: true };

  if (!isAuthUserEmailConfirmed(user)) {
    return { ok: false, reason: "email-not-confirmed" };
  }

  const independent = Boolean(pending.independent_trainer);
  const explicitScopeRaw = __asStr(pending.scope_id);
  // Wizard setzt bei „Neuer Verein“ oft scope_id = NEW_CLUB_PENDING_SCOPE_ID. Das ist kein echter Tenant —
  // ohne diese Ausnahme wäre hasNewClubRequest falsch und es entstünde nur access_requests(__pending_new_club__),
  // unsichtbar für die globale Superadmin-Queue.
  const explicitTenantScope =
    explicitScopeRaw && explicitScopeRaw !== NEW_CLUB_PENDING_SCOPE_ID
      ? explicitScopeRaw
      : "";
  const newClubRaw =
    pending.new_club_request && typeof pending.new_club_request === "object"
      ? pending.new_club_request
      : null;
  const ncName = __asStr(newClubRaw?.club_name);
  const ncCity = __asStr(newClubRaw?.city);
  const hasNewClubRequest = Boolean(ncName && ncCity) && !explicitTenantScope;

  const rawRoles = Array.isArray(pending.requested_roles)
    ? pending.requested_roles
    : [];
  const roles = await __sanitizeSelfAccessRolesForCurrentUser(rawRoles);
  if (!roles.length) return { ok: false, reason: "missing-roles" };

  const coordination = __asStr(pending.trainer_coordination) || "none";
  const noteExtra = __asStr(pending.note);
  const displayName = __asStr(meta.full_name);
  const noteParts = [];
  if (displayName) noteParts.push(`Name: ${displayName}`);
  if (noteExtra) noteParts.push(noteExtra);
  if (hasNewClubRequest) {
    const bits = [`Neuer Verein (Wunsch): ${ncName}`, `Ort: ${ncCity}`];
    const ncWeb = __asStr(newClubRaw?.website);
    const ncContact = __asStr(newClubRaw?.contact);
    if (ncWeb) bits.push(`Website: ${ncWeb}`);
    if (ncContact) bits.push(`Ansprechpartner/Rolle: ${ncContact}`);
    noteParts.push(bits.join("\n"));
  }
  const note = noteParts.length ? noteParts.join("\n") : null;

  const caps = {
    trainer_coordination: coordination,
    requested_app_keys: [],
    registration_self_signup: true,
  };
  if (hasNewClubRequest) {
    caps.registration_new_club_request = {
      club_name: ncName,
      city: ncCity,
      website: __asStr(newClubRaw?.website),
      contact: __asStr(newClubRaw?.contact),
    };
  }

  // Neu-Verein: globale Onboarding-Queue (nicht scope-gebunden / nicht unsichtbar für Superadmin)
  if (hasNewClubRequest && !independent) {
    const appsRaw = Array.isArray(caps.requested_app_keys)
      ? caps.requested_app_keys.map(__asStr).filter(Boolean)
      : [];
    const rpcName = "create_registration_onboarding_request";
    const rpcPayload = {
      p_requested_club_name: ncName,
      p_requested_club_city: ncCity,
      p_requested_roles: roles,
      p_requested_apps: appsRaw.length ? appsRaw : null,
      p_requested_capabilities: caps,
      p_request_note: note,
    };
    // PostgREST: nur public exponiert — RPC-Wrapper in public.* (Migration 20260654000322).
    const { data, error } = await supabase.rpc(rpcName, rpcPayload);
    if (error) {
      return {
        ok: false,
        reason: "rpc-error",
        rpc: rpcName,
        rpcPayload,
        error,
        errorInfo: __supabaseErrorInfo(error),
      };
    }

    const { error: upErr } = await supabase.auth.updateUser({
      data: {
        pending_self_access_request: {
          applied: true,
          applied_at: new Date().toISOString(),
        },
      },
    });
    if (upErr)
      return { ok: false, reason: "metadata-update-failed", error: upErr };

    return {
      ok: true,
      outcome: REGISTRATION_ONBOARDING_OUTCOME,
      registrationResult: data,
    };
  }

  let scopeId = explicitTenantScope;
  if (independent) {
    scopeId = INDEPENDENT_TRAINER_SCOPE_ID;
  }
  if (!scopeId) return { ok: false, reason: "missing-scope" };

  const isRealTenantScope =
    !independent &&
    scopeId &&
    scopeId !== NEW_CLUB_PENDING_SCOPE_ID &&
    scopeId !== INDEPENDENT_TRAINER_SCOPE_ID;

  let tenantHasAdmin = null;
  if (isRealTenantScope) {
    const { data: hasAdmin, error: adminErr } = await callGovernanceRpc(
      supabase,
      "tenant_scope_has_admin",
      {
        p_scope_id: scopeId,
      },
    );
    if (!adminErr) {
      tenantHasAdmin = Boolean(hasAdmin);
    }
    // Sicherheitsmodus: solange kein bestätigter Vereinsadmin feststeht,
    // läuft der Request über Superadmin-Review.
    if (tenantHasAdmin !== true) {
      caps.awaiting_superadmin_review = true;
      caps.superadmin_lead_required = true;
      caps.superadmin_lead_reason =
        tenantHasAdmin === false ? "no-club-admin" : "admin-check-unavailable";
    }
  }

  const marketplaceJoinRolesOnly =
    isRealTenantScope &&
    tenantHasAdmin === true &&
    roles.length > 0 &&
    roles.every((role) => {
      const r = __asStr(role).toLowerCase();
      return r === "player" || r === "parent";
    });

  if (marketplaceJoinRolesOnly) {
    const joinRes = await joinExistingTenantSelfServiceRemote({
      tenantId: scopeId,
      requestedRoles: roles,
    });
    if (!joinRes?.ok) return joinRes;

    const { error: upErr } = await supabase.auth.updateUser({
      data: {
        pending_self_access_request: {
          applied: true,
          applied_at: new Date().toISOString(),
        },
      },
    });
    if (upErr)
      return { ok: false, reason: "metadata-update-failed", error: upErr };

    return { ok: true, joinResult: joinRes };
  }

  const useEmptyClubIds =
    independent ||
    scopeId === NEW_CLUB_PENDING_SCOPE_ID ||
    scopeId === INDEPENDENT_TRAINER_SCOPE_ID;

  const selfRes = await createSelfAccessRequestRemote({
    scope: "tenant",
    scopeId,
    requestedRoles: roles,
    requestedCapabilities: caps,
    requestedClubIds: useEmptyClubIds ? [] : [scopeId],
    note,
  });

  if (!selfRes?.ok) return selfRes;

  const { error: upErr } = await supabase.auth.updateUser({
    data: {
      pending_self_access_request: {
        applied: true,
        applied_at: new Date().toISOString(),
      },
    },
  });
  if (upErr)
    return { ok: false, reason: "metadata-update-failed", error: upErr };

  return { ok: true, request: selfRes.request };
}

/** @type {Promise<{ ok: boolean, requests?: any[], missing?: boolean, reason?: string, error?: unknown }>|null} */
let __loadMyAccessRequestsInflight = null;

export async function loadMyAccessRequestsRemote() {
  if (__loadMyAccessRequestsInflight) return __loadMyAccessRequestsInflight;

  __loadMyAccessRequestsInflight = (async () => {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const rpcRes = await __loadMyAccessRequestsViaRpc(supabase);
    if (rpcRes?.ok) return rpcRes;

    return { ok: true, requests: [], missing: true };
  })();

  try {
    return await __loadMyAccessRequestsInflight;
  } finally {
    __loadMyAccessRequestsInflight = null;
  }
}

export async function loadPendingAccessRequestsForScopeRemote(scope, scopeId) {
  const s = __asStr(scope);
  const sid = __asStr(scopeId);
  if (!s || !sid) return { ok: false, reason: "missing-scope" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await supabase.rpc(
    "list_pending_access_requests_for_scope",
    {
      p_scope: s,
      p_scope_id: sid,
    },
  );

  if (!error) {
    return { ok: true, requests: Array.isArray(data) ? data : [] };
  }

  if (__isMissingTableError(error)) {
    __markAccessRequestsUnavailable(error);
    return { ok: true, requests: [], missing: true };
  }

  return { ok: false, reason: "rpc-error", error };
}

export async function tenantScopeHasAdminRemote(scopeId) {
  const sid = __asStr(scopeId);
  if (!sid) return { ok: false, reason: "missing-scope", hasAdmin: false };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase", hasAdmin: false };

  const { data, error } = await callGovernanceRpc(
    supabase,
    "tenant_scope_has_admin",
    { p_scope_id: sid },
  );
  if (error) return { ok: false, reason: "rpc-error", error, hasAdmin: false };
  return { ok: true, hasAdmin: Boolean(data) };
}

/** Superadmin: pending access_requests für Tenants ohne Vereins-Administrator. */
export async function loadOrphanPendingAccessRequestsRemote() {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase", requests: [] };

  const { data, error } = await callGovernanceRpc(
    supabase,
    "list_orphan_pending_access_requests",
  );
  if (error) return { ok: false, reason: "rpc-error", error, requests: [] };
  const requests = Array.isArray(data) ? data : [];
  return { ok: true, requests };
}

export async function loadAccessRequestHistoryForScopeRemote(scope, scopeId) {
  const s = __asStr(scope);
  const sid = __asStr(scopeId);
  if (!s || !sid) return { ok: false, reason: "missing-scope" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await supabase.rpc(
    "list_access_request_history_for_scope",
    {
      p_scope: s,
      p_scope_id: sid,
    },
  );

  if (!error) {
    return { ok: true, requests: Array.isArray(data) ? data : [] };
  }

  if (__isMissingTableError(error)) {
    __markAccessRequestsUnavailable(error);
    return { ok: true, requests: [], missing: true };
  }

  return { ok: false, reason: "rpc-error", error };
}

export async function approveAccessRequestRemote(
  requestId,
  decisionNote,
  targetScopeId = null,
) {
  const rid = __asStr(requestId);
  if (!rid) return { ok: false, reason: "missing-id" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const ts = targetScopeId != null ? __asStr(targetScopeId) : "";

  const { data, error } = await supabase.rpc("approve_access_request", {
    p_request_id: rid,
    p_decision_note: decisionNote ?? null,
    p_target_scope_id: ts ? ts : null,
  });

  if (error) return { ok: false, reason: "rpc-error", error };
  return { ok: true, result: data || null };
}

export async function rejectAccessRequestRemote(requestId, decisionNote) {
  const rid = __asStr(requestId);
  if (!rid) return { ok: false, reason: "missing-id" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await supabase.rpc("reject_access_request", {
    p_request_id: rid,
    p_decision_note: decisionNote ?? null,
  });

  if (error) return { ok: false, reason: "rpc-error", error };
  return { ok: true, result: data || null };
}

export async function cancelAccessRequestRemote(requestId, note) {
  const rid = __asStr(requestId);
  if (!rid) return { ok: false, reason: "missing-id" };

  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const { data, error } = await supabase.rpc("cancel_access_request", {
    p_request_id: rid,
    p_note: note ?? null,
  });

  if (error) {
    if (__isMissingRpcError(error))
      return { ok: false, reason: "rpc-missing", error };
    return { ok: false, reason: "rpc-error", error };
  }
  return { ok: true, result: data || null };
}

/**
 * Technischer Smoke-Test Helper (ohne UI):
 * - Erzeugt eine Self-Onboarding Access Request (requester=target=auth.uid())
 * - Lädt eigene Requests
 * - Optional: cancel/approve/reject gegen eine konkrete requestId
 *
 * Hinweis: approve/reject erfordern entsprechende Berechtigung (Approver-Rolle im Scope).
 */
export async function runAccessRequestsSmokeRemote({
  scope = "club",
  scopeId,
  requestedRoles = ["trainer"],
  requestedCapabilities = {},
  requestedClubIds = [],
  note = "smoke",
  decisionNote = "smoke",
  action = "create-load",
  requestId = null,
} = {}) {
  const supabase = await getSupabaseClient();
  if (!supabase) return { ok: false, reason: "no-supabase" };

  const uidRes = await __getAuthUserId(supabase);
  if (!uidRes?.ok)
    return {
      ok: false,
      reason: uidRes?.reason || "no-user",
      error: uidRes?.error,
    };

  const uid = uidRes.userId;
  const s = __asStr(scope);
  const sid = __asStr(scopeId);

  const out = {
    ok: false,
    userId: uid,
    createdRequestId: null,
    created: null,
    loaded: null,
    actionResult: null,
  };

  if (action === "create-load" || action === "create-load-cancel") {
    if (!s || !sid) return { ok: false, reason: "missing-scope" };

    const created = await createAccessRequestRemote({
      requester_user_id: uid,
      target_user_id: uid,
      scope: s,
      scope_id: sid,
      requested_roles: Array.isArray(requestedRoles)
        ? requestedRoles
        : [String(requestedRoles)],
      requested_capabilities:
        requestedCapabilities && typeof requestedCapabilities === "object"
          ? requestedCapabilities
          : {},
      requested_club_ids: Array.isArray(requestedClubIds)
        ? requestedClubIds
        : [],
      status: "pending",
      note: __asStr(note) || null,
    });
    out.created = created;
    if (!created?.ok)
      return {
        ...out,
        ok: false,
        reason: created?.reason || "create-failed",
        error: created?.error,
      };

    out.createdRequestId = created?.request?.id || null;
    out.loaded = await loadMyAccessRequestsRemote();
    if (action === "create-load-cancel" && out.createdRequestId) {
      out.actionResult = await cancelAccessRequestRemote(
        out.createdRequestId,
        note,
      );
    }

    return { ...out, ok: true };
  }

  const rid = __asStr(requestId);
  if (!rid) return { ok: false, reason: "missing-id" };

  if (action === "approve") {
    out.actionResult = await approveAccessRequestRemote(rid, decisionNote);
    return {
      ...out,
      ok: Boolean(out.actionResult?.ok),
      reason: out.actionResult?.reason,
      error: out.actionResult?.error,
    };
  }
  if (action === "reject") {
    out.actionResult = await rejectAccessRequestRemote(rid, decisionNote);
    return {
      ...out,
      ok: Boolean(out.actionResult?.ok),
      reason: out.actionResult?.reason,
      error: out.actionResult?.error,
    };
  }
  if (action === "cancel") {
    out.actionResult = await cancelAccessRequestRemote(rid, note);
    return {
      ...out,
      ok: Boolean(out.actionResult?.ok),
      reason: out.actionResult?.reason,
      error: out.actionResult?.error,
    };
  }

  return { ok: false, reason: "unknown-action" };
}
